import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { formatPhoneBR } from '../lib/phone';

type Step = 'phone' | 'continue';

interface LookupResponse {
  exists: boolean;
  name?: string | null;
  phone?: string;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [existingName, setExistingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handlePhoneSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post<LookupResponse>('/auth/lookup', { phone });
      if (data.exists) {
        setExistingName(data.name ?? null);
        setName(data.name ?? '');
      } else {
        setExistingName(null);
        setName('');
      }
      setStep('continue');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao verificar celular');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(phone, name || undefined);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao entrar');
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setStep('phone');
    setError(null);
    setExistingName(null);
  }

  const isReturning = step === 'continue' && !!(existingName && existingName.trim());

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="card w-full max-w-sm space-y-4">
        <h1 className="text-2xl font-bold text-center text-brand-600">Bolão Copa 2026 🏆</h1>

        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              Entre com seu celular pra acessar ou criar sua conta.
            </p>
            <div>
              <label className="block text-sm mb-1">Celular</label>
              <input
                type="tel"
                inputMode="numeric"
                className="input"
                placeholder="(11) 98765-4321"
                value={phone}
                onChange={(e) => setPhone(formatPhoneBR(e.target.value))}
                maxLength={15}
                required
                autoFocus
              />
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <button type="submit" className="btn btn-primary w-full" disabled={loading}>
              {loading ? 'Verificando…' : 'Continuar'}
            </button>
          </form>
        )}

        {step === 'continue' && (
          <form onSubmit={handleLogin} className="space-y-4">
            {isReturning ? (
              <div className="text-center">
                <p className="text-sm text-slate-600">Bem-vindo de volta!</p>
                <p className="text-lg font-semibold mt-1">{existingName || 'Apostador'}</p>
                <p className="text-xs text-slate-500">{formatPhoneBR(phone)}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-600 text-center">
                Primeiro acesso! Como podemos te chamar?
              </p>
            )}

            {!isReturning && (
              <div>
                <label className="block text-sm mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  className="input"
                  placeholder="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={1}
                  autoFocus
                />
              </div>
            )}

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <button type="submit" className="btn btn-primary w-full" disabled={loading}>
              {loading ? 'Entrando…' : isReturning ? 'Entrar' : 'Criar conta e entrar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={handleBack}
              disabled={loading}
            >
              Voltar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
